import React, { FC, useEffect, useState } from 'react';
import { IDateTime } from '../../views/Expenses/expenses.types';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';

import { Field } from 'formik';
import { FilePicker } from '../FilePicker/FilePicker';
import InputField from '../InputField';
import { SimpleDateTimePicker } from '../SimpleDateTimePicker';
import Autocomplete from '@mui/material/Autocomplete';
import { useLazyQuery } from '@apollo/client';
import { GET_USERS_IN_BRANCH } from '../../../graphql/jobSchedular/getJobsQuery';
import { useSelector } from 'react-redux';
import { IAppState } from '../../../store';
import { IUser } from '../../views/Fleet/DamageMatrix/DamageMatrix';
import { DateTime as d } from 'luxon';
import { checkDeleteOrDownloadFileType, checkUploadFileFormat, uploadFileExtensionAndContentType } from '../utils';
import { useSnackBar } from '../SnackBarContext/SnackBarContext';
import { SnackBarVariant } from '../SnackbarWrapper/SnackbarWrapper';
import { getSignedUrl } from '../../../utils/getSignedUrl';

interface IProps {
  isUpdate: boolean,
  vehicleId: string;
  values: any;
  setValues: (values: any) => void;
  showUserInput: boolean;
  setOdometerReading: boolean;
}

const VehicleMileageDetails: FC<IProps> = ({ values, setValues, isUpdate, vehicleId, showUserInput, setOdometerReading }) => {
  const snackbar = useSnackBar();
  const [users, setUsers] = useState<IUser[]>([]);
  const [username, setUsername] = useState("");
  const [odometerStartImageUrl, setOdometerStartImageUrl] = useState("");
  const [odometerEndImageUrl, setOdometerEndImageUrl] = useState("");
  const [minDate, setMinDate] = useState("");
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<Partial<IUser>>();
  const userState = useSelector((state: IAppState) => state.userReducer);
  const { _e_ } = useSelector((state: IAppState) => state.authReducer);

  const [
    getUsersInBranch,
    { loading: branchUsersLoading, data: branchUsersData }
  ] = useLazyQuery(GET_USERS_IN_BRANCH, {
    fetchPolicy: "network-only"
  });

  useEffect(() => {
    getUsersInBranch();
  }, [userState]);

  useEffect(() => {
    if (branchUsersData && branchUsersData.getUsersInBranch?.length) {
      setUsers(branchUsersData.getUsersInBranch);
    }
  }, [branchUsersData]);

  useEffect(() => {
    if (values.vehicleEvent) {
      if (values.vehicleEvent.usedBy) {
        const _user = users.find((user) => user.id === values.vehicleEvent.usedBy);
        if (_user) {
          setSelectedUser(_user);
          setUsername(`${_user.firstName} ${_user.lastName}`);
        }
      }
      if (values.vehicleEvent.odometerReadingAtStartImg) {
        setOdometerStartImageUrl(values.vehicleEvent.odometerReadingAtStartImg);
      }
      if (values.vehicleEvent.odometerReadingAtEndImg) {
        setOdometerEndImageUrl(values.vehicleEvent.odometerReadingAtEndImg);
      }
    }
  }, [users]);


  async function saveOdometerReadingImages(files: File[], type: string) {
    setUploading(true);
    const fileToUpload = files[0]
    try {
      if (checkUploadFileFormat(fileToUpload.type)) {
        const { fileExtension, contentType } = uploadFileExtensionAndContentType(
          fileToUpload.type
        );
        const key = `${vehicleId}-odometer-reading-at-${type}-${d.now().toFormat("dd-MM-yyyy HH:mm")}.${fileExtension}`;
        if (_e_) {
          await _e_
            .add({
              name: key,
              file: fileToUpload,
              complete: async () => {
                if (type === 'start') {
                  setOdometerStartImageUrl(key);
                  setValues({
                    ...values,
                    vehicleEvent: {
                      ...values.vehicleEvent,
                      odometerReadingAtStartImg: key
                    }
                  });
                } else {
                  setOdometerEndImageUrl(key);
                  setValues({
                    ...values,
                    vehicleEvent: {
                      ...values.vehicleEvent,
                      odometerReadingAtEndImg: key
                    }
                  });
                }
                snackbar({
                  message: "Odometer Reading image uploaded.",
                  variant: SnackBarVariant.SUCCESS
                });
                setUploading(false);
              }
            });
        }
      } else {
        setUploading(false);
        return snackbar({
          message: "Please only upload PDF, PNG, JPG or JPEG file types",
          variant: SnackBarVariant.ERROR
        });
      }
    } catch (err) {
      if (err instanceof Error) {
        setUploading(false);
        snackbar({ message: err.message, variant: SnackBarVariant.ERROR });
      }
    }
  }

  async function downloadDocument(documentUrl: string, title: string) {
    setDownloading(true);
    const { fileExtension } = checkDeleteOrDownloadFileType(
      documentUrl
    );
    const file = await getSignedUrl(documentUrl)
    const url: any = file;
    fetch(url, {
      method: "GET"
    })
      .then((response) => response.blob())
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileExtension ? `${title}.${fileExtension}` : title;;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setDownloading(false);
      });
  }

  return (
    <>
      <Grid item xs={12}>
        <Typography variant="h2">Mileage Details</Typography>{" "}
      </Grid>
      <Grid item sm={3}>
        <TextField
          label="Odometer at start"
          name={"odometerAtStart"}
          value={values.vehicleEvent?.odometerReadingAtStart || ""}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            if (setOdometerReading) {
              setValues({
                ...values,
                odometerReading: parseInt(event.target.value) || 0,
                vehicleEvent: {
                  ...values.vehicleEvent,
                  odometerReadingAtStart: parseInt(event.target.value) || 0
                }
              });
            } else {
              setValues({
                ...values,
                vehicleEvent: {
                  ...values.vehicleEvent,
                  odometerReadingAtStart: parseInt(event.target.value) || 0
                }
              });
            }
          }}
          fullWidth
        />
      </Grid>
      <Grid item sm={3}>
        <TextField
          label="Odometer at end"
          name={"odometerAtEnd"}
          value={values.vehicleEvent?.odometerReadingAtEnd || ""}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setValues({
              ...values,
              vehicleEvent: {
                ...values.vehicleEvent,
                odometerReadingAtEnd: parseInt(event.target.value) || 0
              }
            });
          }}
          fullWidth
        />
      </Grid>

      {showUserInput && (
        <Grid item xs={3}>
          <Autocomplete
            id="combo-box-demo"
            options={users}
            getOptionLabel={(option: Partial<IUser>) => {
              if (option) {
                return `${option.firstName} ${option.lastName}`
              } else return "";
            }}
            fullWidth
            onChange={(event: any, newVal: any | null) => {
              if (newVal) {
                setValues({
                  ...values,
                  vehicleEvent: {
                    ...values.vehicleEvent,
                    usedBy: newVal.id
                  }
                })
                setSelectedUser(newVal);
                setUsername(`${newVal.firstName} ${newVal.lastName}`);
              }
            }}
            value={users.find((user) => user.id === values.vehicleEvent?.usedBy) || null}
            inputValue={username}
            onInputChange={(event, newInputValue) => {
              setUsername(newInputValue);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select User"
                fullWidth
                name="user"
              />
            )}
          />
        </Grid>
      )}
      <Grid xs={3} item>
        <FormControlLabel
          control={
            <Switch
              checked={values.vehicleEvent && values.vehicleEvent.isBillable}
              onChange={(event: any) => {
                setValues({
                  ...values,
                  vehicleEvent: {
                    ...values.vehicleEvent,
                    isBillable: event.target.checked
                  }
                })
              }}
              name="inService"
              color="primary"
            />
          }
          label={<Typography variant="body1">{values.vehicleEvent && values.vehicleEvent.isBillable ? "Billable" : "Non Billable"}</Typography>}
        />
      </Grid>
      <Grid item xs={6}>
        <SimpleDateTimePicker
          date={values.vehicleEvent ? values.vehicleEvent.eventStartDateTime : ""}
          handleChange={(date: IDateTime) => {
            setValues({
              ...values,
              vehicleEvent: {
                ...values.vehicleEvent,
                eventStartDateTime: d.fromISO(date.date).toUTC().toISO()
              }
            });
          }}
          required={true}
          disabled={false}
          name={"eventStartDateTime"}
          dateTitle={"Mileage Start Date"}
          timeTitle={"Mileage Start Time"}
          minDate={minDate}
          maxDate={d.now().toUTC().toISO()}
        />
      </Grid>
      <Grid item xs={6}>
        <SimpleDateTimePicker
          date={values.vehicleEvent ? values.vehicleEvent.eventEndDateTime : ""}
          handleChange={(date: IDateTime) => {
            setValues({
              ...values,
              vehicleEvent: {
                ...values.vehicleEvent,
                eventEndDateTime: d.fromISO(date.date).toUTC().toISO()
              }
            });
          }}
          required={true}
          disabled={false}
          name={"eventEndDateTime"}
          dateTitle={"Mileage End Date"}
          timeTitle={"Mileage End Time"}
          minDate={values.vehicleEvent && values.vehicleEvent.eventStartDateTime ?
            values.vehicleEvent.eventStartDateTime : d.now().toUTC().toISO()}
          maxDate={d.now().toUTC().toISO()}
        />
      </Grid>
      <Grid item xs={3} style={{ marginTop: 12 }}>
        <Grid>
          {odometerStartImageUrl && <div>
            {`Start odometer reading image`}
            <IconButton
              disabled={downloading}
              onClick={(e: any) => {
                e.stopPropagation();
                downloadDocument(odometerStartImageUrl, "Start-odometer-reading")
              }}
            >
              <CloudDownloadIcon />
            </IconButton>
          </div>}
          <FilePicker
            onFilePick={(e: any) => {
              if (uploading) {
                return snackbar({
                  message: "Selected image is uplading. Please wait...",
                  variant: SnackBarVariant.ERROR
                });
              }
              saveOdometerReadingImages(e.target.files, 'start');
            }}
            accept={"image/jpeg, image/png, image/jpg, application/pdf"}
            title="UPLOAD START READING"
          />
        </Grid>
        <Box mt={1}></Box>
      </Grid>
      <Grid item xs={3} style={{ marginTop: 12 }}>
        <Grid>
          {odometerEndImageUrl && <div>
            {`End odometer reading image`}
            <IconButton
              disabled={downloading}
              onClick={(e: any) => {
                e.stopPropagation();
                downloadDocument(odometerEndImageUrl, "End-odometer-reading")
              }}
            >
              <CloudDownloadIcon />
            </IconButton>
          </div>}
          <FilePicker
            onFilePick={(e: any) => {
              if (uploading) {
                return snackbar({
                  message: "Selected image is uplading. Please wait...",
                  variant: SnackBarVariant.ERROR
                });
              }
              saveOdometerReadingImages(e.target.files, 'end');
            }}
            accept={"image/jpeg, image/png, image/jpg, application/pdf"}
            title="UPLOAD END READING"
          />
        </Grid>
        <Box mt={1}></Box>
      </Grid>
    </>
  )
}

export default VehicleMileageDetails